import { Node, Edge } from '@xyflow/react'
import { StructuredSpec } from '@/types/recipe'
import { GraphNodeData } from '@/types/graph'

const NODE_WIDTH = 200
const NODE_HEIGHT = 100
const GRID_SIZE = 150

export class RecipeGraphTransformer {
  static specToGraph(spec: StructuredSpec): { nodes: Node<GraphNodeData>[]; edges: Edge[] } {
    const nodes: Node<GraphNodeData>[] = spec.nodes.map((specNode, index) => ({
      id: specNode.id,
      type: specNode.type,
      data: {
        label: specNode.label,
        type: specNode.type,
        config: specNode.config,
      },
      position: specNode.position || {
        x: (index % 3) * GRID_SIZE,
        y: Math.floor(index / 3) * GRID_SIZE,
      },
    }))

    const edges: Edge[] = spec.edges.map((specEdge) => ({
      id: specEdge.id,
      source: specEdge.source,
      target: specEdge.target,
      label: specEdge.label,
      data: {
        label: specEdge.label,
      },
    }))

    return { nodes, edges }
  }

  static graphToSpec(nodes: Node<GraphNodeData>[], edges: Edge[]): StructuredSpec {
    return {
      nodes: nodes.map((node) => ({
        id: node.id,
        type: node.data.type || ('io' as const),
        label: node.data.label,
        config: node.data.config || {},
        position: node.position,
      })),
      edges: edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: typeof edge.label === 'string' ? edge.label : String(edge.label || ''),
        condition: typeof edge.data?.label === 'string' ? edge.data.label : edge.data ? String(edge.data.label || '') : undefined,
      })),
    }
  }

  static validateSpec(spec: StructuredSpec): { valid: boolean; errors: string[] } {
    const errors: string[] = []

    if (!spec.nodes || spec.nodes.length === 0) {
      errors.push('Specification must contain at least one node')
    }

    // Check for duplicate node IDs
    const nodeIds = new Set<string>()
    spec.nodes.forEach((node) => {
      if (nodeIds.has(node.id)) {
        errors.push(`Duplicate node ID: ${node.id}`)
      }
      nodeIds.add(node.id)
    })

    // Check for invalid edge references
    spec.edges.forEach((edge) => {
      if (!nodeIds.has(edge.source)) {
        errors.push(`Edge references non-existent source node: ${edge.source}`)
      }
      if (!nodeIds.has(edge.target)) {
        errors.push(`Edge references non-existent target node: ${edge.target}`)
      }
    })

    return {
      valid: errors.length === 0,
      errors,
    }
  }
}
